import React, { useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';

import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { pdf } from '@react-pdf/renderer';

import GmudPdfTemplatePDF from '~/componentes/TemplatePDF/Gmud';
import FormSectionCard from '~/componentes/FormSectionCard';
import InputText from '~/componentes/Inputs/InputText';
import InputDateTime from '~/componentes/Inputs/InputDateTime';
import InputTime from '~/componentes/Inputs/InputTime';
import InputTextarea from '~/componentes/Inputs/InputTextarea';
import InputCheckbox from '~/componentes/Inputs/InputCheckBox';
import InputRadio from '~/componentes/Inputs/InputRadio';
import InputSelect from '~/componentes/Inputs/InputSelect';
import InputDate from '~/componentes/Inputs/InputDate';
import { SLForm } from '~/pages/Sistema/componentes/SistemaList';
import sistemaListStore, {
  sistemaListShowModal,
} from '~/pages/Sistema/componentes/SistemaList/ThisMainComponent/thisStore';

import api from '~/services/api';
import IconLixeira from '~/assets/icones/IconLixeira.svg';
import ShowConfirmacao from '~/utils/ShowConfirmacao';
import formatDateToSaoPauloTimezone from '~/utils/formatDateToSaoPauloTimezone';

import { Colors } from '~/config/Colors';
import * as S from './styles';

const schema = z
  .object({
    //Dados do Solicitante
    idCliente: z.string().min(1, 'Cliente é obrigatório'),
    idSolicitante: z.string().min(1, 'Solicitante é obrigatório'),
    // solicitanteEmail: z.string().optional(),
    // area: z.string().min(1, 'Área é obrigatória'),s
    dataSolicitacao: z
      .date({
        required_error: 'Data de solicitação é obrigatória',
        invalid_type_error: 'Data de solicitação é obrigatória',
      })
      .transform(val => val.toISOString()),

    //Responsáveis pela Mudança
    idCoordenador: z.string().min(1, 'Coordenador é obrigatório'),
    idExecutor: z.string().min(1, 'Executor é obrigatório'),
    idModuloTotvs: z.string().min(1, 'Módulo TOTVS é obrigatório'),
    idVersaoSistemaTotvs: z.string().optional(),

    dataHoraInicio: z
      .date({
        required_error: 'Data e hora início é obrigatória',
        invalid_type_error: 'Data e hora início é obrigatória',
      })
      .transform(val => val.toISOString()),
    dataHoraFim: z
      .date({
        required_error: 'Data e hora fim é obrigatória',
        invalid_type_error: 'Data e hora fim é obrigatória',
      })
      .transform(val => val.toISOString()),
    status: z.string().min(1, 'Status é obrigatório'),

    //Descrição das Mudanças
    titulo: z.string().min(1, 'Título é obrigatório'),
    descricaoMudanca: z.string().min(1, 'Descrição é obrigatória'),
    descricaoAtividade: z.string().min(1, 'Descrição é obrigatória'),

    // Links TDN TOTVS
    linksTdnTotvs: z
      .array(
        z.object({
          url: z.string().url('URL deve ser válida').optional(),
        }),
      )
      .optional(),

    //Classificações
    atualizacaoDePatch: z.boolean().optional(),
    atualizacaoDeLib: z.boolean().optional(),
    atualizacaoDeStoredProcedure: z.boolean().optional(),
    atualizacaoDeSchemasTss: z.boolean().optional(),
    atualizacaoDeMashup: z.boolean().optional(),
    atualizacaoDeTss: z.boolean().optional(),
    atualizacaoDeUpddistr: z.boolean().optional(),
    compilaFonte: z.boolean().optional(),
    outros: z.boolean().optional(),
    githubHashCommit: z
      .string()
      .optional()
      .refine(
        value => {
          if (!value || value.trim() === '') return true; // Optional field
          return /^[a-fA-F0-9]{40}$/.test(value.trim());
        },
        {
          message:
            'Hash do commit deve ter exatamente 40 caracteres hexadecimais (ex: ffc3ad03eb1496f8a489b9c4873965e1a1540393)',
        },
      )
      .transform(val => (val ? val.trim() : null)),
    outrosClassificacao: z.string().optional(),

    // InputRadio (Categoria, Prioridade, Origem, Ambiente, Finalidade, Haverá Indisponibilidade ,Janela Acordada, Dependência de outra mudança)
    categoria: z
      .string({
        required_error: 'Categoria é obrigatória',
        invalid_type_error: 'Categoria é obrigatória',
      })
      .min(1, 'Categoria é obrigatória'),
    prioridade: z
      .string({
        required_error: 'Prioridade é obrigatória',
        invalid_type_error: 'Prioridade é obrigatória',
      })
      .min(1, 'Prioridade é obrigatória'),
    origem: z
      .string({
        required_error: 'Origem é obrigatória',
        invalid_type_error: 'Origem é obrigatória',
      })
      .min(1, 'Origem é obrigatória'),
    numeroTicket: z.string().optional(),
    ambiente: z
      .string({
        required_error: 'Ambiente é obrigatório',
        invalid_type_error: 'Ambiente é obrigatório',
      })
      .min(1, 'Ambiente é obrigatório'),
    finalidade: z
      .string({
        required_error: 'Finalidade é obrigatória',
        invalid_type_error: 'Finalidade é obrigatória',
      })
      .min(1, 'Finalidade é obrigatória'),
    janelaAcordada: z
      .string({
        required_error: 'Janela Acordada é obrigatória',
        invalid_type_error: 'Janela Acordada é obrigatória',
      })
      .min(1, 'Janela Acordada é obrigatória'),
    janelaAcordadaComQuem: z.string().optional(),
    dependeDeOutraMudanca: z
      .string({
        required_error: 'Informe se depende de outra mudança',
        invalid_type_error: 'Informe se depende de outra mudança',
      })
      .min(1, 'Informe se depende de outra mudança'),
    dependeDeOutraMudancaQual: z.string().optional(),
    indisponibilidade: z
      .string({
        required_error: 'Informe se haverá indisponibilidade',
        invalid_type_error: 'Informe se haverá indisponibilidade',
      })
      .min(1, 'Informe se haverá indisponibilidade'),
    tempoIndisponibilidade: z
      .number()
      .min(1, 'Tempo de indisponibilidade é obrigatório')
      .optional(),

    // Plano de execução da mudança
    atividades: z.array(
      z.object({
        descricao: z.string().min(1, 'Descrição da atividade é obrigatória'),
        idResponsavel: z.string().min(1, 'Responsável é obrigatório'),
        dataHoraInicio: z
          .date({
            required_error: 'Data e hora início é obrigatório',
            invalid_type_error: 'Data e hora início é obrigatória',
          })
          .transform(val => val.toISOString()),
        tempoPrevistoEmMinutos: z.number().min(1, 'Tempo previsto é obrigatório'),
      }),
    ),

    // Plano de validação da mudança
    validacao: z.array(
      z.object({
        descricao: z.string().min(1, 'Descrição da ação é obrigatória'),
        resultado: z.string().min(1, 'Resultado esperado é obrigatório'),
        passfail: z.string().optional(),
        comentario: z.string().optional(),
      }),
    ),

    // Plano de Rollback (para o caso de problemas)
    rollback: z.array(
      z.object({
        descricao: z.string().min(1, 'Descrição da ação é obrigatória'),
        idResponsavel: z.string().min(1, 'Responsável é obrigatório'),
        datahora: z
          .date({
            required_error: 'Data e hora é obrigatório',
            invalid_type_error: 'Data e hora é obrigatório',
          })
          .transform(val => val.toISOString()),
        comentario: z.string().optional(),
      }),
    ),

    // Plano de Escalonamento em Caso de Problemas
    escalonamento: z.array(
      z.object({
        idResponsavel: z.string().optional(),
        nome: z.string().min(1, 'Nome é obrigatório'),
        telefone: z.string(),
      }),
    ),
  })
  .superRefine((data, ctx) => {
    if (data.outros && !data.outrosClassificacao) {
      ctx.addIssue({
        path: ['outrosClassificacao'],
        code: z.ZodIssueCode.custom,
        message: 'Especifique o outro tipo de classificação.',
      });
    }
    if (data.janelaAcordada === 'sim' && !data.janelaAcordadaComQuem) {
      ctx.addIssue({
        path: ['janelaAcordadaComQuem'],
        code: z.ZodIssueCode.custom,
        message: 'Informe com quem foi a janela acordada.',
      });
    }
    if (data.dependeDeOutraMudanca === 'sim' && !data.dependeDeOutraMudancaQual) {
      ctx.addIssue({
        path: ['dependeDeOutraMudancaQual'],
        code: z.ZodIssueCode.custom,
        message: 'Informe a qual mudança depende.',
      });
    }
    if (data.compilaFonte && (!data.githubHashCommit || data.githubHashCommit.trim() === '')) {
      ctx.addIssue({
        path: ['githubHashCommit'],
        code: z.ZodIssueCode.custom,
        message: 'O hash do commit do GitHub é obrigatório.',
      });
    }
    if (data.indisponibilidade === 'sim' && !data.tempoIndisponibilidade) {
      ctx.addIssue({
        path: ['tempoIndisponibilidade'],
        code: z.ZodIssueCode.custom,
        message: 'Informe o tempo de indisponibilidade.',
      });
    }
    if (data.origem === 'ticket' && (!data.numeroTicket || data.numeroTicket.trim() === '')) {
      ctx.addIssue({
        path: ['numeroTicket'],
        code: z.ZodIssueCode.custom,
        message: 'Número do ticket é obrigatório.',
      });
    }
    if (data.ambiente === 'producao' && !data.idVersaoSistemaTotvs) {
      ctx.addIssue({
        path: ['idVersaoSistemaTotvs'],
        code: z.ZodIssueCode.custom,
        message: 'Selecione a versão do sistema TOTVS.',
      });
    }
  });

function CreateContent({
  templateData,
  setTemplateData,
  refetch,
  perfilAtual,
  analistas,
  clientesSolicitantes,
  coordenadores,
  clientes,
  modulosTotvs,
  versoesSistemaTotvs,
}) {
  const { perfil } = useSelector(state => state.auth);
  const { state, dispatch } = React.useContext(sistemaListStore);

  const {
    register,
    control,
    formState: { errors },
    setError,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: templateData
      ? templateData
      : {
          idCoordenador: String(perfilAtual?.coordenador?.id || ''),
          idExecutor: String(perfilAtual?.perfil?.usuario?.id || ''),
          status: 'pendente',
          atividades: [
            {
              idResponsavel: String(perfilAtual?.perfil?.usuario?.id || ''),
              tempoPrevistoEmMinutos: 0,
            },
          ],
          validacao: [{}],
          rollback: [
            {
              descricao: 'Backup do RPO',
              idResponsavel: String(perfilAtual?.perfil?.usuario?.id || ''),
            },
          ],
          escalonamento: [
            {
              idResponsavel: String(perfilAtual?.perfil?.usuario?.id || ''),
              nome: perfilAtual?.nome || '',
              telefone: perfilAtual?.perfil?.usuario?.telefone1 || '',
            },
          ],
        },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'atividades',
  });
  const {
    fields: fieldsValidacao,
    append: appendValidacao,
    remove: removeValidacao,
  } = useFieldArray({
    control,
    name: 'validacao',
  });
  const {
    fields: fieldsRollback,
    append: appendRollback,
    remove: removeRollback,
  } = useFieldArray({
    control,
    name: 'rollback',
  });
  const {
    fields: fieldsEscalonamento,
    append: appendEscalonamento,
    remove: removeEscalonamento,
  } = useFieldArray({
    control,
    name: 'escalonamento',
  });
  const {
    fields: fieldsLinksTdnTotvs,
    append: appendLinksTdnTotvs,
    remove: removeLinksTdnTotvs,
  } = useFieldArray({
    control,
    name: 'linksTdnTotvs',
  });

  const dataSolicitacao = watch('dataSolicitacao');
  const dataHoraInicio = watch('dataHoraInicio');
  const indisponibilidadeValue = watch('indisponibilidade');
  const janelaAcordadaValue = watch('janelaAcordada');
  const dependeDeOutraMudancaValue = watch('dependeDeOutraMudanca');
  const outrosValue = watch('outros');
  const compilaFonteValue = watch('compilaFonte');
  const solicitanteValue = watch('idSolicitante');
  const clienteValue = watch('idCliente');
  const origemValue = watch('origem');
  const ambienteValue = watch('ambiente');

  async function handleSubmit(data) {
    // const { closeReload } = options || {};

    const perfilResponse = await api.get('/signed/empresa/administracao/gerenciamento/perfil', {
      params: { id: perfil.idPerfil, idEmpresa: perfil.idEmpres },
    });

    const body = {
      ...data,
      area: data?.area || '',
      idEmpresa: perfil.idEmpresa,
      idUnidade: perfil.idUnidade,
      idSolicitante: Number(data.idSolicitante),
      idCoordenador: Number(data.idCoordenador),
      idExecutor: Number(data.idExecutor),
      idUsuarioCadastro: perfilResponse.data?.idUsuario,
      idModuloTotvs: Number(data.idModuloTotvs),
      idVersaoSistemaTotvs: data.ambiente === 'producao' ? Number(data.idVersaoSistemaTotvs) : null,

      githubHashCommit: data?.compilaFonte ? data.githubHashCommit : null,
      outraClassificacao: data?.outros ? data.outrosClassificacao : null,

      tempoIndisponibilidadeEmMinutos:
        data?.indisponibilidade === 'sim' ? data.tempoIndisponibilidade : null,
      janelaAcordadaComQuem: data?.janelaAcordada === 'sim' ? data.janelaAcordadaComQuem : null,
      dependeDeOutraMudancaQual:
        data?.dependeDeOutraMudanca === 'sim' ? data.dependeDeOutraMudancaQual : null,

      atividades: data.atividades.map(atividadeItem => ({
        ...atividadeItem,
        idResponsavel: Number(atividadeItem.idResponsavel),
      })),
      validacao: data.validacao.map(validacaoItem => ({
        ...validacaoItem,
        passfail:
          validacaoItem?.passfail === 'pass' ? 1 : validacaoItem?.passfail === 'fail' ? 0 : null,
      })),
      rollback: data.rollback.map(rollbackItem => ({
        ...rollbackItem,
        idResponsavel: Number(rollbackItem.idResponsavel),
      })),
      escalonamento: data.escalonamento.map(escalonamento => ({
        idResponsavel: Number(escalonamento.idResponsavel),
      })),
    };

    return ShowConfirmacao(
      async () => {
        let response;
        try {
          response = await api.post('/signed/empresa/gestorprojeto/gmud', body);
        } catch (error) {
          console.error('Erro ao criar GMUD:', error);
          Sentry.captureException(error);
        }

        refetch && refetch();

        if (body?.status === 'concluida') {
          try {
            const gmudData = response.data;
            const clientesSolicitanteData =
              clientesSolicitantes.find(c => c.id === Number(solicitanteValue)) || {};

            const bodyDataGmudDocument = {
              ...data,
              dataHoraInicio: formatDateToSaoPauloTimezone(data?.dataHoraInicio),
              dataHoraFim: formatDateToSaoPauloTimezone(data?.dataHoraFim),
              outraClassificacao: data?.outros ? data?.outrosClassificacao : '',
              clienteSolicitanteGmud: clientesSolicitanteData,
              tempoIndisponibilidadeEmMinutos:
                data?.indisponibilidade === 'sim' ? data.tempoIndisponibilidade : null,
              linksTdnTotvs: data?.linksTdnTotvs || [],
              execucao: data?.atividades?.map(execucaoItem => {
                const analista = analistas?.find(
                  a => a?.perfil?.usuario?.id === Number(execucaoItem.idResponsavel),
                );
                return {
                  responsavel: {
                    nome: analista?.nome ?? '',
                  },
                  descricao: execucaoItem?.descricao,
                  dataHoraInicio: formatDateToSaoPauloTimezone(execucaoItem?.dataHoraInicio),
                  tempoPrevistoEmMinutos: execucaoItem?.tempoPrevistoEmMinutos,
                };
              }),
              rollback: data.rollback.map(rollbackItem => {
                const analista = analistas?.find(
                  a => a?.perfil?.usuario?.id === Number(rollbackItem.idResponsavel),
                );
                return {
                  ...rollbackItem,
                  datahora: formatDateToSaoPauloTimezone(rollbackItem?.datahora),
                  responsavel: {
                    nome: analista?.nome ?? '',
                  },
                };
              }),
              escalonamento: data?.escalonamento?.map(escalonamentoItem => {
                const analista = analistas.find(
                  a => a?.perfil?.usuario?.id === Number(escalonamentoItem?.idResponsavel),
                );
                return {
                  responsavel: {
                    nome: analista?.nome ?? '',
                    telefone: analista?.telefone ?? '',
                    email: analista?.perfil?.usuario?.email ?? '',
                  },
                };
              }),
              idGmud: gmudData.id,
            };

            const pdfBlob = await pdf(
              <GmudPdfTemplatePDF key={Date.now()} {...bodyDataGmudDocument} />,
            ).toBlob();

            const reader = new FileReader();
            reader.readAsDataURL(pdfBlob);

            reader.onloadend = async () => {
              const base64data = reader.result.split(',')[1];

              await api.post('/signed/empresa/gestorprojeto/gmud/send-email', {
                gmudId: gmudData.id,
                pdfBase64: base64data,
                emailSubject: `GMUD #${gmudData.id} - ${gmudData.titulo}`,
                emailBody: `Uma nova GMUD foi criada: ${gmudData.titulo}`,
              });
            };
          } catch (error) {
            console.error('Erro ao enviar email com PDF da GMUD:', error);
            Sentry.captureException(error);
          }
        }

        if (response?.status === 200) {
          state.reloadData();
          dispatch(sistemaListShowModal(false));
        }

        return response;
      },
      {
        errorModal: {
          defaultMessage: `Ocorreu um erro ao cadastrar a sua GMUD. Por favor, tente novamente.`,
        },
        message: `Tem Certeza que deseja Criar a GMUD?`,
        successModal: `GMUD criada com sucesso!`,
      },
    );
  }

  const clienteListSolicitante = useMemo(
    () =>
      clientesSolicitantes
        ?.filter(c => c.idCliente === Number(clienteValue))
        ?.map(solicitante => ({
          value: String(solicitante.id),
          label: solicitante.nome,
        })) || [],
    [clientesSolicitantes, clienteValue],
  );

  const clienteList = useMemo(
    () =>
      clientes?.map(client => ({
        value: String(client.id),
        label: client.nomeFantasia,
      })),
    [clientes],
  );

  const analistaListFormatted = useMemo(
    () =>
      analistas.map(analista => ({
        value: String(analista?.perfil?.usuario?.id),
        label: analista?.perfil?.usuario?.nome,
      })),
    [analistas],
  );

  const modulosTotvsFormatted = useMemo(
    () =>
      modulosTotvs?.map(sistema => ({
        value: String(sistema.id),
        label: sistema.nome + ' - ' + sistema.descricao,
      })) || [],
    [modulosTotvs],
  );

  const versoesSistemaTotvsFormatted = useMemo(
    () =>
      versoesSistemaTotvs?.map(versao => ({
        value: String(versao.id),
        label: versao.nome,
      })) || [],
    [versoesSistemaTotvs],
  );

  useEffect(() => {
    if (templateData) {
      setTemplateData(undefined);
    }
  }, [templateData]);

  return (
    <SLForm onSubmit={handleFormSubmit(handleSubmit)} defaultStyle>
      <S.BodyPageContainer>
        <FormSectionCard title="Dados do Solicitante">
          <S.CardBodyContainer>
            <Controller
              control={control}
              name="idCliente"
              render={({ field }) => (
                <InputSelect
                  label="Cliente *"
                  options={[{ value: '', label: 'Selecione o cliente' }, ...clienteList]}
                  value={field.value}
                  onChange={field.onChange}
                  required
                  error={errors?.idCliente?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="idSolicitante"
              render={({ field }) => (
                <InputSelect
                  label="Solicitante *"
                  options={[
                    { value: '', label: 'Selecione o solicitante' },
                    ...clienteListSolicitante,
                  ]}
                  value={field.value}
                  onChange={field.onChange}
                  required
                  error={errors.idSolicitante?.message}
                />
              )}
            />
            {/* <InputText
              label="Email"
              placeholder="Email do solicitante"
              {...register('solicitanteEmail')}
              error={errors?.solicitanteEmail?.message}
              readOnly
            /> */}
            <Controller
              control={control}
              name="idModuloTotvs"
              render={({ field }) => (
                <InputSelect
                  label="Módulos *"
                  options={[
                    { value: '', label: 'Selecione o módulo TOTVS' },
                    ...modulosTotvsFormatted,
                  ]}
                  value={field.value}
                  onChange={field.onChange}
                  required
                  error={errors.idModuloTotvs?.message}
                />
              )}
            />
            {/* <InputText
              label="Área *"
              placeholder="Digite a área do solicitante"
              {...register('area')}
              error={errors.area?.message}
              required
            /> */}
            <Controller
              control={control}
              name={`dataSolicitacao`}
              render={({ field }) => (
                <InputDate
                  label="Data da solicitação *"
                  placeholder="Data da solicitação"
                  error={errors?.dataSolicitacao?.message}
                  required
                  {...field}
                />
              )}
            />
          </S.CardBodyContainer>
        </FormSectionCard>

        {/* Vou adicionar o resto do formulário completo aqui */}
        <FormSectionCard title="Controle e Responsáveis da GMUD">
          <S.ResponsaveisMudancaContainer>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <InputSelect
                  label="Status *"
                  options={[
                    { value: '', label: 'Todos os status' },
                    { value: 'pendente', label: 'Pendente' },
                    { value: 'concluida', label: 'Concluída' },
                    // { value: 'cancelada', label: 'Cancelada' },
                  ]}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.status?.message}
                  labelTooltip="Você pode criar, editar e excluir GMUDs enquanto estiverem pendentes. Após concluir, um documento será enviado ao cliente, solicitante e coordenador. Se cancelar, será necessário informar o motivo e a GMUD não poderá mais ser editada."
                />
              )}
            />
            <Controller
              control={control}
              name="idCoordenador"
              render={({ field }) => (
                <InputSelect
                  label="Coordenador *"
                  options={coordenadores.map(coordenador => ({
                    value: String(coordenador?.id ?? ''),
                    label: coordenador?.nome,
                  }))}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.idCoordenador?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="idExecutor"
              render={({ field }) => (
                <InputSelect
                  label="Executor *"
                  options={analistaListFormatted}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.idExecutor?.message}
                />
              )}
            />
            <Controller
              control={control}
              name={`dataHoraInicio`}
              render={({ field }) => (
                <InputDateTime
                  label="Data e Hora Início *"
                  placeholder="Data e Hora Início"
                  minDate={dataSolicitacao}
                  required
                  error={errors?.dataHoraInicio?.message}
                  {...field}
                />
              )}
            />
            <Controller
              control={control}
              name={`dataHoraFim`}
              render={({ field }) => (
                <InputDateTime
                  label="Data e Hora Fim *"
                  {...field}
                  onChange={value => {
                    setError('dataHoraFim', {});

                    if (value && dataHoraInicio) {
                      const data = new Date(value);
                      const dataInicio = new Date(dataHoraInicio);

                      if (data < dataInicio) {
                        setError('dataHoraFim', {
                          type: 'manual',
                          message:
                            'A data e hora de fim deve ser posterior à data e hora de início',
                        });

                        return;
                      }
                    }

                    field.onChange(value);
                  }}
                  placeholder="Data e Hora Fim"
                  minDate={dataHoraInicio}
                  disabled={!dataHoraInicio}
                  required
                  error={errors?.dataHoraFim?.message}
                />
              )}
            />
          </S.ResponsaveisMudancaContainer>
        </FormSectionCard>

        {/* Descrição das Mudanças */}
        <FormSectionCard
          title="Descrição das Mudanças"
          subTitle="Detalhe o que será alterado e as atividades envolvidas."
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 16 }}>
            <InputText
              label="Título *"
              placeholder="Digite o título da GMUD"
              {...register('titulo')}
              error={errors.titulo?.message}
              required
            />
            <InputTextarea
              label="Descrição da Mudança *"
              placeholder="Digite a descrição da mudança"
              {...register('descricaoMudanca')}
              error={errors.descricaoMudanca?.message}
              required
              rows={2}
            />
            <InputTextarea
              label="Descrição da Atividade *"
              placeholder="Digite a descrição da atividade"
              {...register('descricaoAtividade')}
              error={errors.descricaoAtividade?.message}
              required
            />
          </div>

          <S.Line />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
            <S.HeaderWrapper>
              <S.TitleWrapper>
                <S.CardTitle>Links TDN TOTVS</S.CardTitle>
                <S.CardSubTitle
                  style={{
                    marginTop: -4,
                  }}
                >
                  Adicione links relevantes da documentação TDN TOTVS
                </S.CardSubTitle>
              </S.TitleWrapper>

              <S.AddButton type="button" onClick={() => appendLinksTdnTotvs({ url: '' })}>
                <i
                  className="material-icons"
                  style={{
                    fontSize: '28px',
                    color: localStorage.getItem('tema') == 'E' ? '#ffffff' : '#6796cf',
                  }}
                >
                  add
                </i>
              </S.AddButton>
            </S.HeaderWrapper>

            {fieldsLinksTdnTotvs.map((field, index) => (
              <div key={field.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <InputText
                    placeholder="https://suporte.totvs.com/..."
                    {...register(`linksTdnTotvs.${index}.url`)}
                    error={errors.linksTdnTotvs?.[index]?.url?.message}
                    style={{ paddingRight: '40px' }}
                  />
                </div>
                <S.DeleteButton
                  title="Remover"
                  type="button"
                  onClick={() => removeLinksTdnTotvs(index)}
                >
                  <img src={IconLixeira} alt="lixeira" />
                </S.DeleteButton>
              </div>
            ))}
          </div>
        </FormSectionCard>

        {/* Classificações */}
        <FormSectionCard
          title="Classificações"
          subTitle="Selecione as classificações adequadas para a GMUD"
          isLine
        >
          <S.ClassificacoesContainer>
            <InputCheckbox
              label="Atualização de Patch"
              {...register('atualizacaoDePatch')}
              error={errors.atualizacaoDePatch?.message}
            />
            <InputCheckbox
              label="Atualização de LIB"
              {...register('atualizacaoDeLib')}
              error={errors.atualizacaoDeLib?.message}
            />
            <InputCheckbox
              label="Atualização de Stored Procedure"
              {...register('atualizacaoDeStoredProcedure')}
              error={errors.atualizacaoDeStoredProcedure?.message}
            />
            <InputCheckbox
              label="Atualização de Schemas TSS"
              {...register('atualizacaoDeSchemasTss')}
              error={errors.atualizacaoDeSchemasTss?.message}
            />
            <InputCheckbox
              label="Atualização de Mashup"
              {...register('atualizacaoDeMashup')}
              error={errors.atualizacaoDeMashup?.message}
            />
            <InputCheckbox
              label="Atualização de TSS"
              {...register('atualizacaoDeTss')}
              error={errors.atualizacaoDeTss?.message}
            />
            <InputCheckbox
              label="Atualização de UPDDISTR"
              {...register('atualizacaoDeUpddistr')}
              error={errors.atualizacaoDeUpddistr?.message}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <InputCheckbox
                label="Compila Fonte"
                {...register('compilaFonte', {
                  onChange: e => {
                    if (!e.target.checked) {
                      setValue('githubHashCommit', '');
                    }
                  },
                })}
                error={errors.compilaFonte?.message}
              />
              {compilaFonteValue && (
                <InputText
                  placeholder="Ex: ffc3ad03eb1496f8a489b9c4873965e1a1540393"
                  {...register('githubHashCommit')}
                  error={errors.githubHashCommit?.message}
                  required
                  styleContainer={{
                    paddingRight: '24px',
                    width: '60%',
                  }}
                />
              )}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <InputCheckbox
                label={`Outros ${outrosValue ? ':' : ''}`}
                {...register('outros')}
                error={errors.outros?.message}
              />
              {outrosValue && (
                <InputText
                  placeholder="Digite o outro tipo de classificação"
                  {...register('outrosClassificacao')}
                  error={errors.outrosClassificacao?.message}
                  required
                  styleContainer={{
                    paddingRight: '36px',
                    width: '70%',
                  }}
                />
              )}
            </div>
          </S.ClassificacoesContainer>
        </FormSectionCard>

        {/* Radios Container */}
        <S.RadiosContainer>
          <FormSectionCard title="Categoria" isLine>
            <S.RadioContent>
              <InputRadio label="Agendada" value="agendada" {...register('categoria')} />
              <InputRadio label="Emergencial" value="emergencial" {...register('categoria')} />
              <InputRadio label="Padrão" value="padrao" {...register('categoria')} />
              {errors.categoria && (
                <span style={{ color: Colors.ftColor6, fontSize: 12 }}>
                  {errors.categoria.message}
                </span>
              )}
            </S.RadioContent>
          </FormSectionCard>

          <FormSectionCard title="Prioridade" isLine>
            <S.RadioContent>
              <InputRadio label="Baixa" value="baixa" {...register('prioridade')} />
              <InputRadio label="Média" value="media" {...register('prioridade')} />
              <InputRadio label="Alta" value="alta" {...register('prioridade')} />
              {errors.prioridade && (
                <span style={{ color: Colors.ftColor6, fontSize: 12 }}>
                  {errors.prioridade.message}
                </span>
              )}
            </S.RadioContent>
          </FormSectionCard>

          <FormSectionCard title="Origem" isLine>
            <S.RadioContent
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <InputRadio label="Projeto" value="projeto" {...register('origem')} />
                <InputRadio label="Incidente" value="incidente" {...register('origem')} />
              </div>
              <InputRadio label="Produto" value="produto" {...register('origem')} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <InputRadio
                  label="Ticket"
                  value="ticket"
                  {...register('origem')}
                  error={errors.origem?.message}
                />
                {origemValue === 'ticket' && (
                  <InputText
                    placeholder="Digite o número do ticket"
                    {...register('numeroTicket')}
                    error={errors.numeroTicket?.message}
                    styleContainer={{
                      marginTop: '-8px',
                      height: '24px',
                    }}
                    style={{
                      height: '24px',
                    }}
                  />
                )}
              </div>
            </S.RadioContent>
          </FormSectionCard>

          <FormSectionCard title="Ambiente" isLine>
            <S.RadioContent twoColumns>
              <InputRadio label="Produção" value="producao" {...register('ambiente')} />
              <InputRadio
                label="Desenvolvimento"
                value="desenvolvimento"
                {...register('ambiente')}
              />
              {errors.ambiente && (
                <span style={{ color: Colors.ftColor6, fontSize: 12 }}>
                  {errors.ambiente.message}
                </span>
              )}
            </S.RadioContent>

            <div>
              <Controller
                control={control}
                name="idVersaoSistemaTotvs"
                render={({ field }) => (
                  <InputSelect
                    label="Versão do Sistema TOTVS"
                    options={[
                      { value: '', label: 'Selecione a versão' },
                      ...versoesSistemaTotvsFormatted,
                    ]}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.idVersaoSistemaTotvs?.message}
                    isDisabled={ambienteValue !== 'producao'}
                  />
                )}
              />
            </div>
          </FormSectionCard>
        </S.RadiosContainer>

        {/* Segunda linha de radios */}
        <S.RadiosContainer>
          <FormSectionCard title="Finalidade" isLine>
            <S.RadioContent twoColumns>
              <InputRadio label="Melhoria" value="melhoria" {...register('finalidade')} />
              <InputRadio label="Corretiva" value="corretiva" {...register('finalidade')} />
              <InputRadio label="Projeto" value="projeto" {...register('finalidade')} />
              <InputRadio label="Upgrade" value="upgrade" {...register('finalidade')} />
              <InputRadio label="Teste" value="teste" {...register('finalidade')} />
              <InputRadio label="Preventiva" value="preventiva" {...register('finalidade')} />

              {errors.finalidade && (
                <span style={{ color: Colors.ftColor6, fontSize: 12 }}>
                  {errors.finalidade.message}
                </span>
              )}
            </S.RadioContent>
          </FormSectionCard>

          <FormSectionCard title="Haverá Indisponibilidade?" isLine>
            <S.RadioContent>
              <InputRadio label="Não" value="nao" {...register('indisponibilidade')} />
              <InputRadio label="Sim" value="sim" {...register('indisponibilidade')} />
              {errors.indisponibilidade && (
                <span style={{ color: Colors.ftColor6, fontSize: 12 }}>
                  {errors.indisponibilidade.message}
                </span>
              )}
              <Controller
                control={control}
                name={`tempoIndisponibilidade`}
                render={({ field }) => (
                  <InputTime
                    name={field.name}
                    label={`Tempo de Indisponibilidade ${
                      indisponibilidadeValue === 'sim' ? '*' : ''
                    }`}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="HH:MM"
                    disabled={indisponibilidadeValue !== 'sim'}
                    error={errors.tempoIndisponibilidade?.message}
                    styleContainer={{ marginTop: '8px' }}
                  />
                )}
              />
            </S.RadioContent>
          </FormSectionCard>

          <FormSectionCard title="Janela Acordada" isLine>
            <S.RadioContent>
              <InputRadio label="Não" value="nao" {...register('janelaAcordada')} />
              <InputRadio label="Sim" value="sim" {...register('janelaAcordada')} />
              {errors.janelaAcordada && (
                <span style={{ color: Colors.ftColor6, fontSize: 12 }}>
                  {errors.janelaAcordada.message}
                </span>
              )}
              <InputText
                label={`Com Quem? ${janelaAcordadaValue === 'sim' ? '*' : ''}`}
                placeholder="Digite com quem a janela foi acordada"
                {...register('janelaAcordadaComQuem')}
                error={errors.janelaAcordadaComQuem?.message}
                disabled={janelaAcordadaValue !== 'sim'}
                styleContainer={{
                  marginTop: '8px',
                }}
              />
            </S.RadioContent>
          </FormSectionCard>

          <FormSectionCard title="Depende de outra mudança?" isLine>
            <S.RadioContent>
              <InputRadio label="Não" value="nao" {...register('dependeDeOutraMudanca')} />
              <InputRadio label="Sim" value="sim" {...register('dependeDeOutraMudanca')} />
              {errors.dependeDeOutraMudanca && (
                <span style={{ color: Colors.ftColor6, fontSize: 12 }}>
                  {errors.dependeDeOutraMudanca.message}
                </span>
              )}
              <InputText
                label={`Qual? ${dependeDeOutraMudancaValue === 'sim' ? '*' : ''}`}
                placeholder="Informe de qual mudança depende"
                {...register('dependeDeOutraMudancaQual')}
                error={errors.dependeDeOutraMudancaQual?.message}
                disabled={dependeDeOutraMudancaValue !== 'sim'}
                styleContainer={{
                  marginTop: '8px',
                }}
              />
            </S.RadioContent>
          </FormSectionCard>
        </S.RadiosContainer>

        {/* Plano de Execução */}
        <FormSectionCard
          title="Plano de Execução da Mudança"
          onClickButton={() =>
            append({
              descricao: '',
              idResponsavel: String(perfilAtual?.perfil?.usuario?.id || ''),
              dataHoraInicio: null,
              tempoPrevistoEmMinutos: 0,
            })
          }
        >
          {fields.map((field, index) => (
            <S.CardPlanoContainer key={field.id}>
              <S.CardPlanoTitleContent>
                <p>Atividade - {index + 1}</p>
                <S.DeleteButton
                  title="Remover"
                  type="button"
                  onClick={() => fields.length > 1 && remove(index)}
                >
                  <img src={IconLixeira} alt="lixeira" />
                </S.DeleteButton>
              </S.CardPlanoTitleContent>
              <S.CardPlanoContent
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 0.4fr 0.3fr 0.3fr',
                  width: '100%',
                  alignItems: 'flex-start',
                  gap: '16px',
                }}
              >
                <InputText
                  label="Atividade *"
                  placeholder="Digite a descrição da atividade"
                  {...register(`atividades.${index}.descricao`)}
                  error={errors.atividades?.[index]?.descricao?.message}
                  required
                />
                <Controller
                  control={control}
                  name={`atividades.${index}.idResponsavel`}
                  render={({ field }) => (
                    <InputSelect
                      label="Responsável *"
                      options={analistaListFormatted}
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.atividades?.[index]?.idResponsavel?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`atividades.${index}.dataHoraInicio`}
                  render={({ field }) => (
                    <InputDateTime
                      label="Data e Hora Início *"
                      {...field}
                      onChange={value => {
                        setError(`atividades.${index}.dataHoraInicio`, {});

                        if (value && dataHoraInicio) {
                          const data = new Date(value);
                          const dataInicio = new Date(dataHoraInicio);

                          if (data < dataInicio) {
                            setError(`atividades.${index}.dataHoraInicio`, {
                              type: 'manual',
                              message:
                                'A data e hora da atividade deve ser posterior à data e hora de início da GMUD',
                            });

                            return;
                          }
                        }

                        field.onChange(value);
                      }}
                      placeholder="Data e Hora Início"
                      minDate={dataHoraInicio}
                      disabled={!dataHoraInicio}
                      required
                      error={errors.atividades?.[index]?.dataHoraInicio?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={`atividades.${index}.tempoPrevistoEmMinutos`}
                  render={({ field }) => (
                    <InputTime
                      name={field.name}
                      label="Tempo Previsto *"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="HH:MM"
                      // disabled={!dataHoraInicio}
                      error={errors.atividades?.[index]?.tempoPrevistoEmMinutos?.message}
                    />
                  )}
                />
              </S.CardPlanoContent>
            </S.CardPlanoContainer>
          ))}
        </FormSectionCard>

        {/* Plano de Validação */}
        <FormSectionCard
          title="Plano de Validação da Mudança"
          onClickButton={() =>
            appendValidacao({
              descricao: '',
              resultado: '',
              passfail: '',
              comentario: '',
            })
          }
        >
          {fieldsValidacao.map((field, index) => (
            <S.CardPlanoContainer key={field.id}>
              <S.CardPlanoTitleContent>
                <p>Ação - {index + 1}</p>
                <S.DeleteButton
                  title="Remover"
                  type="button"
                  onClick={() => fieldsValidacao.length > 1 && removeValidacao(index)}
                >
                  <img src={IconLixeira} alt="lixeira" />
                </S.DeleteButton>
              </S.CardPlanoTitleContent>
              <S.CardPlanoContent style={{ flexDirection: 'column' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '0.3fr 1fr',
                    width: '100%',
                    gap: '16px',
                  }}
                >
                  <Controller
                    control={control}
                    name={`validacao.${index}.passfail`}
                    render={({ field }) => (
                      <InputSelect
                        label="Passou / Falhou"
                        options={[
                          { value: '', label: 'Selecione' },
                          { value: 'pass', label: 'Passou' },
                          { value: 'fail', label: 'Falhou' },
                        ]}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.validacao?.[index]?.passfail?.message}
                      />
                    )}
                  />
                  <InputText
                    label="Descrição da Ação *"
                    placeholder="Digite a descrição"
                    {...register(`validacao.${index}.descricao`)}
                    error={errors.validacao?.[index]?.descricao?.message}
                    required
                  />
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '0.3fr 1fr',
                    width: '100%',
                    gap: '16px',
                  }}
                >
                  <InputText
                    label="Comentário"
                    placeholder="Comentário"
                    {...register(`validacao.${index}.comentario`)}
                    error={errors.validacao?.[index]?.comentario?.message}
                    style={{ width: '100%' }}
                  />
                  <InputText
                    label="Resultado Esperado *"
                    placeholder="Digite o resultado esperado"
                    {...register(`validacao.${index}.resultado`)}
                    error={errors.validacao?.[index]?.resultado?.message}
                    required
                  />
                </div>
              </S.CardPlanoContent>
            </S.CardPlanoContainer>
          ))}
        </FormSectionCard>

        {/* Plano de Rollback */}
        <FormSectionCard
          title="Plano de Rollback (para o caso de problemas)"
          onClickButton={() =>
            appendRollback({
              descricao: '',
              idResponsavel: String(perfilAtual?.perfil?.usuario?.id || ''),
              datahora: null,
              comentario: '',
            })
          }
        >
          {fieldsRollback.map((field, index) => (
            <S.CardPlanoContainer key={field.id}>
              <S.CardPlanoTitleContent>
                <p>Ação - {index + 1}</p>
                <S.DeleteButton
                  title="Remover"
                  type="button"
                  onClick={() => fieldsRollback.length > 1 && removeRollback(index)}
                >
                  <img src={IconLixeira} alt="lixeira" />
                </S.DeleteButton>
              </S.CardPlanoTitleContent>
              <S.CardPlanoContent style={{ flexDirection: 'column' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '0.3fr 1fr',
                    width: '100%',
                    gap: '16px',
                  }}
                >
                  <Controller
                    control={control}
                    name={`rollback.${index}.datahora`}
                    render={({ field }) => (
                      <InputDateTime
                        label="Data/Hora *"
                        required
                        {...field}
                        onChange={value => {
                          setError(`rollback.${index}.datahora`, {});
                          if (value && dataHoraInicio) {
                            const data = new Date(value);
                            const dataInicio = new Date(dataHoraInicio);

                            if (data < dataInicio) {
                              setError(`rollback.${index}.datahora`, {
                                type: 'manual',
                                message:
                                  'A data e hora do rollback deve ser posterior à data e hora de início da GMUD',
                              });

                              return;
                            }
                          }

                          field.onChange(value);
                        }}
                        placeholder="Data e hora"
                        minDate={dataHoraInicio}
                        disabled={!dataHoraInicio}
                        error={errors.rollback?.[index]?.datahora?.message}
                      />
                    )}
                  />
                  <InputText
                    label="Descrição da Ação *"
                    placeholder="Digite a descrição"
                    {...register(`rollback.${index}.descricao`)}
                    error={errors.rollback?.[index]?.descricao?.message}
                    required
                  />
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '0.3fr 1fr',
                    width: '100%',
                    gap: '16px',
                  }}
                >
                  <Controller
                    control={control}
                    name={`rollback.${index}.idResponsavel`}
                    render={({ field }) => (
                      <InputSelect
                        label="Responsável *"
                        options={analistaListFormatted}
                        value={field.value}
                        onChange={e => field.onChange(e)}
                        error={errors.rollback?.[index]?.idResponsavel?.message}
                      />
                    )}
                  />
                  <InputText
                    label="Comentário"
                    placeholder="Comentário"
                    {...register(`rollback.${index}.comentario`)}
                    error={errors.rollback?.[index]?.comentario?.message}
                  />
                </div>
              </S.CardPlanoContent>
            </S.CardPlanoContainer>
          ))}
        </FormSectionCard>

        {/* Plano de Escalonamento */}
        <FormSectionCard
          title="Plano de Escalonamento em Caso de Problemas"
          onClickButton={() => appendEscalonamento({ idResponsavel: '', nome: '', telefone: '' })}
        >
          {fieldsEscalonamento.map((field, index) => (
            <S.CardPlanoContainer key={field.id}>
              <S.CardPlanoTitleContent>
                <p>Contato - {index + 1}</p>

                <S.DeleteButton
                  title="Remover"
                  type="button"
                  onClick={() => fieldsEscalonamento.length > 1 && removeEscalonamento(index)}
                >
                  <img src={IconLixeira} alt="lixeira" />
                </S.DeleteButton>
              </S.CardPlanoTitleContent>
              <S.CardPlanoContent>
                <Controller
                  control={control}
                  name={`escalonamento.${index}.idResponsavel`}
                  render={({ field }) => (
                    <InputSelect
                      label="Responsável *"
                      options={analistaListFormatted}
                      value={field.value}
                      onChange={e => {
                        field.onChange(e);
                        const selectedValue = e.target.value;

                        const selectedAnalista = analistas.find(
                          a => a.perfil?.usuario?.id === Number(selectedValue),
                        );
                        if (selectedAnalista) {
                          setValue(`escalonamento.${index}.nome`, selectedAnalista.nome || '');
                          setValue(
                            `escalonamento.${index}.telefone`,
                            selectedAnalista.perfil?.usuario?.telefone1 || '',
                          );
                        }
                      }}
                      error={errors.escalonamento?.[index]?.idResponsavel?.message}
                    />
                  )}
                />
                <InputText
                  label="Nome *"
                  placeholder="Digite o nome"
                  {...register(`escalonamento.${index}.nome`)}
                  error={errors.escalonamento?.[index]?.nome?.message}
                  required
                  readOnly
                />
                <InputText
                  label="Telefone"
                  placeholder="Digite o telefone"
                  {...register(`escalonamento.${index}.telefone`)}
                  error={errors.escalonamento?.[index]?.telefone?.message}
                  required
                  readOnly
                />
              </S.CardPlanoContent>
            </S.CardPlanoContainer>
          ))}
        </FormSectionCard>
      </S.BodyPageContainer>
    </SLForm>
  );
}

export default CreateContent;
